import React, { useEffect, useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import CardBody from '@/components/ui/cards/CardBody';
import { Input } from '@/components/ui/inputs/input';
import InputRadio from '@/components/ui/inputs/input-radio';
import PermissionCheck from '@/components/common/PermissionCheck';

import { getSettingService, updateSettingService } from '@/services/SettingServices';
import validationErrors from '@/lib/validationErrors';
import { AXIOS_BASE_URL } from '@/security/Axios';

import {
  listHrmDevices,
  createHrmDevice,
  updateHrmDevice,
  deleteHrmDevice,
  toggleHrmDevice,
} from '@/services/HrmDeviceService';

const KEYS = [
  'hrm_zk_enabled',
  // NOTE: hrm_zk_ip is legacy and not used by the puller; keep only for backwards-compat display if you want.
  // 'hrm_zk_ip',
  'hrm_zk_port',
  'hrm_zk_timeout_ms',
  'hrm_pull_interval_sec',
  'hrm_pull_interval_min', // legacy (read only)
  'hrm_timezone',
  'hrm_late_grace_min',
  'hrm_absent_grace_min',
];

const DEFAULTS = {
  hrm_zk_enabled: '1',
  // hrm_zk_ip: '',
  hrm_zk_port: '4370',
  hrm_zk_timeout_ms: '10000',
  hrm_pull_interval_sec: '300',
  hrm_timezone: 'Africa/Mogadishu',
  hrm_late_grace_min: '10',
  hrm_absent_grace_min: '0',
};

const api = axios.create({ baseURL: AXIOS_BASE_URL });

/* ---------------- tenant helpers ---------------- */
function computeTenantIds(userData, general) {
  const reduxCompany = userData?.company_id || userData?.companyId || general?.company?._id;
  const reduxBranch  = userData?.branch_id || userData?.default_branch_id || general?.branch?._id || general?.selectedBranch?._id;

  const lsCompany = localStorage.getItem('company_id') || localStorage.getItem('X-Company-Id');
  const lsBranch  = localStorage.getItem('branch_id')  || localStorage.getItem('X-Branch-Id');

  let jwtCompany, jwtBranch;
  try {
    const tok = localStorage.getItem('jwt_token');
    if (tok) {
      const d = jwtDecode(tok) || {};
      jwtCompany = d?.company_id;
      jwtBranch  = d?.branch_id;
    }
  } catch {}

  const companyId = String(reduxCompany || lsCompany || jwtCompany || '').trim() || undefined;
  const branchId  = String(reduxBranch  || lsBranch  || jwtBranch  || '').trim() || undefined;

  return { companyId, branchId };
}

function useTenantHeaders() {
  const userData = useSelector((s) => s.Auth?.userData);
  const general  = useSelector((s) => s.GeneralSetting);

  const { companyId, branchId } = useMemo(() => computeTenantIds(userData, general), [userData, general]);

  return useMemo(() => {
    const headers = {};
    const token = localStorage.getItem('jwt_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (companyId) headers['X-Company-Id'] = companyId;
    if (branchId) headers['X-Branch-Id'] = branchId;
    return headers;
  }, [companyId, branchId]);
}

/* ---------------- Device Config Panel (CRUD per branch) ---------------- */
function DeviceConfigPanel() {
  const headers = useTenantHeaders();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  // local edit/new form state
  const [form, setForm] = useState({ alias: '', ip: '', port: 4370, enabled: true, _id: '' });

  async function load() {
    setBusy(true);
    try {
      const res = await listHrmDevices(headers);
      const data = Array.isArray(res?.data) ? res.data : [];
      setRows(data);
      if (data.length) {
        const d = data[0];
        setForm({ _id: String(d._id), alias: d.alias || '', ip: d.ip || '', port: d.port || 4370, enabled: !!d.enabled });
      } else {
        setForm({ _id: '', alias: '', ip: '', port: 4370, enabled: true });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      Swal.fire('Error', 'Failed to load device(s)', 'error');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [headers['X-Company-Id'], headers['X-Branch-Id']]);

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === 'checkbox' ? checked : value }));
  }

  async function onSave() {
    if (!form.ip) return Swal.fire('Validation', 'Device IP is required', 'warning');
    const payload = {
      alias: form.alias || null,
      ip: String(form.ip).trim(),
      port: Number(form.port || 4370) || 4370,
      enabled: !!form.enabled,
    };
    try {
      setBusy(true);
      if (form._id) {
        await updateHrmDevice(form._id, payload, headers);
      } else {
        await createHrmDevice(payload, headers);
      }
      await api.post('/hrm/reload-config', {}, { headers }); // hot-reload scheduler for this branch
      await load();
      Swal.fire('Saved', 'Device saved for this branch', 'success');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      Swal.fire('Error', 'Failed to save device', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onToggle() {
    if (!form._id) return;
    try {
      setBusy(true);
      await toggleHrmDevice(form._id, headers);
      await api.post('/hrm/reload-config', {}, { headers });
      await load();
      Swal.fire('OK', 'Toggled device', 'success');
    } catch {
      Swal.fire('Error', 'Toggle failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!form._id) return;
    const ask = await Swal.fire({ title: 'Delete device?', text: 'Soft-delete this device for the branch.', icon: 'warning', showCancelButton: true });
    if (!ask.isConfirmed) return;

    try {
      setBusy(true);
      await deleteHrmDevice(form._id, headers);
      await api.post('/hrm/reload-config', {}, { headers });
      await load();
      Swal.fire('Deleted', 'Device removed', 'success');
    } catch {
      Swal.fire('Error', 'Delete failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader isBorder>
        <div className="flex items-center justify-between">
          <h4 className="db-card-title">Device Configuration (per Branch)</h4>
          <span className="text-xs text-slate-500">
            Scope: <b>{headers['X-Company-Id'] || '-'}</b> / <b>{headers['X-Branch-Id'] || '-'}</b>
          </span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            labelText="Alias"
            labelStyleClassName="db-field-title"
            name="alias"
            value={form.alias}
            onChange={onChange}
            size="sm"
            placeholder="e.g. Reception"
          />
          <Input
            labelText="Device IP"
            labelStyleClassName="db-field-title required"
            name="ip"
            value={form.ip}
            onChange={onChange}
            size="sm"
            placeholder="192.168.10.101"
          />
          <Input
            labelText="Port"
            labelStyleClassName="db-field-title required"
            type="number"
            name="port"
            value={form.port}
            onChange={onChange}
            size="sm"
            placeholder="4370"
          />
          <div className="form-input-item">
            <label className="db-field-title">Enabled</label>
            <div className="db-field-radio-group flex gap-4">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" name="enabled" checked={!!form.enabled} onChange={onChange} />
                <span>Device is enabled</span>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button className="db-btn bg-primary text-white" onClick={onSave} disabled={busy}>
            Save Device
          </button>
          <button className="db-btn bg-secondary" onClick={load} disabled={busy}>
            Refresh
          </button>
          <button className="db-btn bg-secondary" onClick={onToggle} disabled={busy || !form._id}>
            Toggle Enable
          </button>
          <button className="db-btn bg-danger" onClick={onDelete} disabled={busy || !form._id}>
            Delete
          </button>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          {rows.length ? (
            <div>Found {rows.length} device(s) for this branch. Puller will use enabled, non-deleted rows.</div>
          ) : (
            <div>No device yet for this branch. Create one above.</div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------- Device Sync Panel (unchanged behavior) ---------------- */
function DevicePanel() {
  const tokenFromRedux = useSelector((s) => s.Auth?.token);
  const token = tokenFromRedux || localStorage.getItem('jwt_token');
  const headers = useTenantHeaders();

  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function loadStatus(toast = false) {
    try {
      const res = await api.get('/hrm/status', { headers });
      setStatus(res.data?.data ?? null);
      if (toast) Swal.fire('Refreshed', 'Device status updated', 'success');
    } catch {
      setStatus(null);
      if (toast) Swal.fire('Error', 'Failed to load device status', 'error');
    }
  }

  useEffect(() => {
    loadStatus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, headers['X-Company-Id'], headers['X-Branch-Id']]);

  const enabled = !!status?.enabled;
  const lastRun = status?.lastRunAt ? new Date(status.lastRunAt) : null;
  const lastErr = status?.lastError || '—';
  const lastInfo = status?.lastResult
    ? `rows=${status.lastResult?.rowsSeen ?? 0}, processed=${status.lastResult?.processed ?? 0}, duplicates=${status.lastResult?.duplicates ?? 0}, matched=${status.lastResult?.matchedAdmins ?? 0}`
    : '—';

  async function handleToggle() {
    setBusy(true);
    setMsg('');
    try {
      const res = await api.post('/hrm/toggle', { enabled: !enabled }, { headers });
      setStatus(res.data?.data ?? null);
      Swal.fire('OK', `Auto-pull ${!enabled ? 'enabled' : 'disabled'} for this branch`, 'success');
    } catch {
      Swal.fire('Error', 'Failed to toggle auto-pull', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handlePullNow() {
    setBusy(true);
    setMsg('');
    try {
      const res = await api.post('/hrm/pull-once', {}, { headers });
      const d = res.data?.data || {};
      setMsg(`Pulled: rows=${d.rowsSeen ?? 0}, processed=${d.processed ?? 0}, duplicates=${d.duplicates ?? 0}, matched=${d.matchedAdmins ?? 0}`);
      await loadStatus(false);
      Swal.fire('Done', 'Device logs pulled successfully', 'success');
    } catch {
      Swal.fire('Error', 'Pull failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleReload() {
    setBusy(true);
    try {
      await api.post('/hrm/reload-config', {}, { headers });
      await loadStatus(false);
      Swal.fire('Reloaded', 'Scheduler reloaded with current branch settings', 'success');
    } catch {
      Swal.fire('Error', 'Failed to reload scheduler', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleClearLogs() {
    setBusy(true);
    try {
      await api.post('/hrm/clear-logs', {}, { headers });
      Swal.fire('Cleared', 'Device logs cleared', 'success');
    } catch {
      Swal.fire('Error', 'Failed to clear logs', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader isBorder>
        <div className="flex items-center justify-between">
          <h4 className="db-card-title">Device Sync</h4>
          <span className="text-xs text-slate-500">
            Scope: <b>{headers['X-Company-Id'] || '-'}</b> / <b>{headers['X-Branch-Id'] || '-'}</b>
          </span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm">
            <div className="flex items-center justify-between py-1">
              <span className="text-slate-500">Enabled</span>
              <span className="font-medium text-slate-800">{String(!!enabled)}</span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-slate-500">Interval</span>
              <span className="font-medium text-slate-800">
                {status?.intervalMs ? `${Math.round(status.intervalMs / 1000)}s` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-slate-500">Last Run</span>
              <span className="font-medium text-slate-800">{lastRun ? lastRun.toLocaleString() : '—'}</span>
            </div>
          </div>

          <div className="text-sm">
            <div className="flex items-center justify-between py-1">
              <span className="text-slate-500">Last Error</span>
              <span className="font-medium text-slate-800 truncate max-w-[260px]" title={lastErr}>
                {lastErr}
              </span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-slate-500">Last Result</span>
              <span className="font-medium text-slate-800 truncate max-w-[260px]" title={lastInfo}>
                {lastInfo}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button className="db-btn bg-secondary" onClick={handleToggle} disabled={busy || !status}>
              {enabled ? 'Disable Auto-Pull' : 'Enable Auto-Pull'}
            </button>
            <button className="db-btn bg-primary" onClick={handlePullNow} disabled={busy}>
              Pull Now
            </button>
            <button className="db-btn bg-secondary" onClick={handleReload} disabled={busy}>
              Reload
            </button>
            <button className="db-btn bg-danger" onClick={handleClearLogs} disabled={busy}>
              Clear Logs
            </button>
            <button className="db-btn bg-secondary" onClick={() => loadStatus(true)} disabled={busy}>
              Refresh
            </button>
          </div>
        </div>

        {msg && <div className="mt-3 text-slate-600 text-sm">{msg}</div>}
      </CardBody>
    </Card>
  );
}

/* ---------------- Main HRM Settings Page ---------------- */
const HRMSettingsPage = ({ pageTitle = 'HRM' }) => {
  const dispatch = useDispatch();
  const headers = useTenantHeaders();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({ defaultValues: DEFAULTS });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers['X-Company-Id'], headers['X-Branch-Id']]);

  const fetchSettings = () => {
    setLoading(true);
    dispatch(getSettingService({ key_list: KEYS }))
      .then((res) => {
        const raw = res?.data?.data || {};
        const next = { ...DEFAULTS };

        ['hrm_zk_enabled', 'hrm_zk_port', 'hrm_zk_timeout_ms', 'hrm_timezone', 'hrm_late_grace_min', 'hrm_absent_grace_min'].forEach((k) => {
          if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') next[k] = String(raw[k]);
        });

        if (raw.hrm_pull_interval_sec) {
          next.hrm_pull_interval_sec = String(raw.hrm_pull_interval_sec);
        } else if (raw.hrm_pull_interval_min) {
          const sec = Math.max(1, Number(raw.hrm_pull_interval_min) * 60 || 300);
          next.hrm_pull_interval_sec = String(sec);
        }

        reset(next);
        setLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setLoading(false);
      });
  };

  const onSubmit = async (form) => {
    setSaving(true);
    const payload = { ...form };

    payload.hrm_zk_enabled = String(payload.hrm_zk_enabled ?? '1');
    payload.hrm_zk_port = String(Number(payload.hrm_zk_port || 4370));
    payload.hrm_zk_timeout_ms = String(Number(payload.hrm_zk_timeout_ms || 10000));
    payload.hrm_pull_interval_sec = String(Math.max(1, Number(payload.hrm_pull_interval_sec) || 300));
    payload.hrm_late_grace_min = String(Math.max(0, parseInt(payload.hrm_late_grace_min ?? '0', 10) || 0));
    payload.hrm_absent_grace_min = String(Math.max(0, parseInt(payload.hrm_absent_grace_min ?? '0', 10) || 0));
    delete payload.hrm_pull_interval_min;

    try {
      await dispatch(updateSettingService(payload));
      await api.post('/hrm/reload-config', {}, { headers }); // hot-reload for this scope
      Swal.fire('Saved', 'Settings saved for this branch', 'success');
    } catch ({ message, errorData }) {
      validationErrors(errorData, message);
    } finally {
      setSaving(false);
    }
  };

  const enabledVal = watch('hrm_zk_enabled');

  return (
    <>
      <Card>
        <CardHeader isBorder>
          <div className="flex items-center justify-between">
            <h3 className="db-card-title">{pageTitle}</h3>
            <span className="text-xs text-slate-500">
              Writing to scope: <b>{headers['X-Company-Id'] || '-'}</b> / <b>{headers['X-Branch-Id'] || '-'}</b>
            </span>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Controller
                control={control}
                name="hrm_zk_enabled"
                render={({ field }) => (
                  <div className="form-input-item">
                    <label className="db-field-title required">ZKT Device Puller</label>
                    <div className="db-field-radio-group flex gap-4">
                      <InputRadio
                        id="zk_on"
                        name={field.name}
                        value="1"
                        checked={String(field.value) === '1'}
                        onChange={field.onChange}
                        labelText="Enabled"
                      />
                      <InputRadio
                        id="zk_off"
                        name={field.name}
                        value="0"
                        checked={String(field.value) === '0'}
                        onChange={field.onChange}
                        labelText="Disabled"
                      />
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="hrm_zk_port"
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Default Port"
                    labelStyleClassName="db-field-title required"
                    type="number"
                    size="sm"
                    placeholder="4370"
                    errorType={errors?.hrm_zk_port?.type}
                  />
                )}
              />

              <Controller
                control={control}
                name="hrm_zk_timeout_ms"
                rules={{ required: true, min: 1000 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Timeout (ms)"
                    labelStyleClassName="db-field-title required"
                    type="number"
                    size="sm"
                    placeholder="10000"
                    errorType={errors?.hrm_zk_timeout_ms?.type}
                  />
                )}
              />

              <Controller
                control={control}
                name="hrm_pull_interval_sec"
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Pull Interval (seconds)"
                    labelStyleClassName="db-field-title required"
                    type="number"
                    size="sm"
                    placeholder="300"
                    errorType={errors?.hrm_pull_interval_sec?.type}
                  />
                )}
              />

              <Controller
                control={control}
                name="hrm_timezone"
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Timezone"
                    labelStyleClassName="db-field-title required"
                    size="sm"
                    placeholder="Africa/Mogadishu"
                    errorType={errors?.hrm_timezone?.type}
                  />
                )}
              />

              <Controller
                control={control}
                name="hrm_late_grace_min"
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Late Grace (minutes)"
                    labelStyleClassName="db-field-title required"
                    type="number"
                    size="sm"
                    placeholder="10"
                    errorType={errors?.hrm_late_grace_min?.type}
                  />
                )}
              />

              <Controller
                control={control}
                name="hrm_absent_grace_min"
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    labelText="Absent Grace (minutes)"
                    labelStyleClassName="db-field-title required"
                    type="number"
                    size="sm"
                    placeholder="0"
                    errorType={errors?.hrm_absent_grace_min?.type}
                  />
                )}
              />
            </div>

            <PermissionCheck permsission={'update_setting'}>
              <div className="mt-6">
                <button className="db-btn text-white bg-primary" type="submit" disabled={saving || loading}>
                  <i className="lab lab-save" />
                  <span>Save Changes</span>
                </button>
              </div>
            </PermissionCheck>

            <div className="mt-3 text-xs text-amber-600">
              Note: Device IP/Port are now configured per branch in the <b>Device Configuration</b> section below.
              The puller ignores legacy <code>hrm_zk_ip</code> setting.
            </div>
          </form>
        </CardBody>
      </Card>

      <PermissionCheck permsission={'update_setting'}>
        <DeviceConfigPanel />
        <DevicePanel />
      </PermissionCheck>
    </>
  );
};

export default HRMSettingsPage;
